import { useState } from "react";
import { Share2, Copy, MessageCircle, Check, Gift } from "lucide-react";
import { toast } from "sonner";

const SHARE_URL = "https://sayitbetter.me";
const SHARE_MESSAGE = "I've been using SayItBetter to check how my texts might land before sending them. Join early access and lock in Pro for $5.99 for the first year: https://sayitbetter.me";

export default function ShareSection({ compact = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = SHARE_URL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTikTokShare = () => {
    // Copy link first for easy pasting
    handleCopyLink();
    // Open TikTok
    window.open("https://www.tiktok.com", "_blank", "noopener,noreferrer");
    toast.info("Link copied! Paste it in your TikTok post.");
  };

  const handleTextShare = () => {
    const smsBody = encodeURIComponent(SHARE_MESSAGE);
    
    // Check if it's iOS or Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // iOS uses & for body
      window.location.href = `sms:&body=${smsBody}`;
    } else {
      // Android uses ? for body
      window.location.href = `sms:?body=${smsBody}`;
    }
  };

  if (compact) {
    return (
      <div className="flex items-center justify-center gap-2">
        <span className="text-xs text-[#6B7280]">Share:</span>
        <button
          onClick={handleTikTokShare}
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          title="Share on TikTok"
          data-testid="share-tiktok-compact"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
          </svg>
        </button>
        <button
          onClick={handleCopyLink}
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          title="Copy Link"
          data-testid="share-copy-compact"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
        <button
          onClick={handleTextShare}
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          title="Share via Text"
          data-testid="share-text-compact"
        >
          <MessageCircle className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#F7F9FB] to-white rounded-2xl p-5 border border-gray-100">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Share2 className="text-[#4FB3A5]" size={18} />
        <h3 className="font-semibold text-[#1F3A5F] text-sm">
          Invite Friends — Lock in the Founder Deal
        </h3>
      </div>
      
      {/* Subtitle */}
      <p className="text-xs text-[#6B7280] mb-4 leading-relaxed">
        Share SayItBetter with friends. Early users get Pro for <span className="font-semibold text-[#4FB3A5]">$5.99</span> for the entire first year when we launch.
      </p>

      {/* Share Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        {/* TikTok */}
        <button
          onClick={handleTikTokShare}
          className="flex-1 flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
          data-testid="share-tiktok-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
          </svg>
          Share on TikTok
        </button>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className="flex-1 flex items-center justify-center gap-2 bg-[#4FB3A5] hover:bg-[#3d9a8d] text-white py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
          data-testid="share-copy-btn"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Link copied!" : "Copy Link"}
        </button>

        {/* Text Message */}
        <button
          onClick={handleTextShare}
          className="flex-1 flex items-center justify-center gap-2 bg-[#1F3A5F] hover:bg-[#2a4a75] text-white py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
          data-testid="share-text-btn"
        >
          <MessageCircle size={16} />
          Share via Text
        </button>
      </div>

      {/* Referral Reward Placeholder */}
      <div className="flex items-center justify-center gap-2 text-xs text-[#A0A7B4] pt-3 border-t border-gray-100">
        <Gift size={14} />
        <span>Invite friends. Referral rewards and bonus free checks are coming soon.</span>
      </div>
    </div>
  );
}
